import React, { useEffect, useState } from "react";
import { Tooltip } from "@aqilliz-dev/aqilliz-ui";
import { formatDateTime, getRelativeTimeShort } from "../../utils";

// One shared 30s clock for every mounted RelativeTime instance, rather than
// each row in a table (this is also rendered per-row in several containers)
// running its own setInterval.
const tickListeners = new Set<() => void>();
let tickIntervalId: ReturnType<typeof setInterval> | null = null;

const subscribeToTick = (listener: () => void) => {
  tickListeners.add(listener);
  if (!tickIntervalId) {
    tickIntervalId = setInterval(() => {
      tickListeners.forEach((l) => l());
    }, 30_000);
  }
  return () => {
    tickListeners.delete(listener);
    if (tickListeners.size === 0 && tickIntervalId) {
      clearInterval(tickIntervalId);
      tickIntervalId = null;
    }
  };
};

interface RelativeTimeProps {
  value: string;
  style?: React.CSSProperties;
}

const RelativeTime: React.FC<RelativeTimeProps> = ({ value, style }) => {
  // getRelativeTimeShort only recomputes on render, so without this tick the
  // label would freeze at whatever it read on mount (e.g. stuck at "1m ago").
  const [, forceTick] = useState(0);
  useEffect(() => subscribeToTick(() => forceTick((n) => n + 1)), []);

  const date = new Date(value);
  const full = isNaN(date.getTime()) ? value : formatDateTime(date);
  const relative = getRelativeTimeShort(date);

  return (
    <Tooltip content={full}>
      <span style={{ cursor: "default", whiteSpace: "nowrap", ...style }}>
        {relative}
      </span>
    </Tooltip>
  );
};

export default RelativeTime;
